/**
 * Content Security Policy Configuration
 * Implements comprehensive CSP headers for security hardening
 */

export interface CSPDirectives {
  'default-src'?: string[];
  'script-src'?: string[];
  'style-src'?: string[];
  'img-src'?: string[];
  'connect-src'?: string[];
  'font-src'?: string[];
  'object-src'?: string[];
  'media-src'?: string[];
  'frame-src'?: string[];
  'child-src'?: string[];
  'worker-src'?: string[];
  'manifest-src'?: string[];
  'base-uri'?: string[];
  'form-action'?: string[];
  'frame-ancestors'?: string[];
  'upgrade-insecure-requests'?: boolean;
  'block-all-mixed-content'?: boolean;
}

export interface CSPConfig {
  development: CSPDirectives;
  production: CSPDirectives;
  reportOnly: boolean;
  reportUri?: string;
  nonce?: string;
}

// Default CSP configuration for the AI Telemetry Dashboard
export const defaultCSPConfig: CSPConfig = {
  reportOnly: false,
  reportUri: '/api/v1/security/csp-report',
  development: {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      "'unsafe-inline'", // Allow inline scripts in development
      "'unsafe-eval'", // Allow eval for development tools
      'https://cdn.jsdelivr.net', // CDN for development dependencies
      'https://unpkg.com'
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Allow inline styles
      'https://fonts.googleapis.com',
      'https://cdn.jsdelivr.net'
    ],
    'img-src': [
      "'self'",
      'data:', // Allow data URLs for images
      'https:', // Allow HTTPS images
      'blob:' // Allow blob URLs for generated images
    ],
    'connect-src': [
      "'self'",
      'https://api.briefcase.ai',
      'https://lakefs.briefcase.ai',
      'https://cognito-idp.us-east-1.amazonaws.com', // AWS Cognito User Pools
      'https://cognito-identity.us-east-1.amazonaws.com', // AWS Cognito Identity Pools
      'https://us.i.posthog.com', // PostHog analytics
      'https://us.posthog.com',
      'https://us-assets.i.posthog.com', // PostHog assets
      'http://ai-telemetry-staging-alb-2144268840.us-east-1.elb.amazonaws.com', // Staging ALB
      'wss://localhost:*', // WebSocket for development
      'ws://localhost:*',
      'https://localhost:*',
      'http://localhost:*',
      'ws:', // Allow all WebSocket connections in dev
      'wss:' // Allow all secure WebSocket connections in dev
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com',
      'data:'
    ],
    'object-src': ["'none'"],
    'media-src': ["'self'", 'blob:'],
    'frame-src': [
      "'self'",
      'https://briefcase.ai' // For OAuth frames
    ],
    'worker-src': ["'self'", 'blob:'],
    'manifest-src': ["'self'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    'frame-ancestors': ["'none'"]
  },
  production: {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      'https://cdn.jsdelivr.net',
      'https://us-assets.i.posthog.com', // PostHog scripts
      "'unsafe-inline'" // Allow Vite-generated inline scripts in production
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Tailwind requires inline styles
      'https://fonts.googleapis.com'
    ],
    'img-src': [
      "'self'",
      'data:',
      'https://briefcase.ai',
      'https://cdn.briefcase.ai'
    ],
    'connect-src': [
      "'self'",
      'https://api.briefcase.ai',
      'https://lakefs.briefcase.ai',
      'https://analytics.briefcase.ai',
      'https://telemetry.briefcasebrain.com', // Telemetry API
      'https://cognito-idp.us-east-1.amazonaws.com', // AWS Cognito User Pools
      'https://cognito-identity.us-east-1.amazonaws.com', // AWS Cognito Identity Pools
      'https://us.i.posthog.com', // PostHog analytics
      'https://us.posthog.com',
      'https://us-assets.i.posthog.com', // PostHog assets
      'http://ai-telemetry-staging-alb-2144268840.us-east-1.elb.amazonaws.com', // Staging ALB
      'wss://ws.briefcase.ai',
      'ws:', // Allow WebSocket connections
      'wss:' // Allow secure WebSocket connections
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com',
      'data:'
    ],
    'object-src': ["'none'"],
    'media-src': ["'self'"],
    'frame-src': [
      'https://briefcase.ai',
      'https://oauth.briefcase.ai'
    ],
    'worker-src': ["'self'"],
    'manifest-src': ["'self'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'", 'https://auth.briefcase.ai'],
    'frame-ancestors': ["'none'"],
    'upgrade-insecure-requests': true,
    'block-all-mixed-content': true
  }
};

export class CSPService {
  private static instance: CSPService;
  private config: CSPConfig;
  private nonce: string | null = null;

  private constructor(config: CSPConfig = defaultCSPConfig) {
    this.config = config;
    this.generateNonce();
  }

  static getInstance(config?: CSPConfig): CSPService {
    if (!CSPService.instance) {
      CSPService.instance = new CSPService(config);
    }
    return CSPService.instance;
  }

  /**
   * Generate a cryptographically secure nonce for inline scripts/styles
   */
  private generateNonce(): void {
    if (typeof window !== 'undefined' && window.crypto) {
      const array = new Uint8Array(16);
      window.crypto.getRandomValues(array);
      this.nonce = btoa(String.fromCharCode(...array));
    } else {
      // Fallback for environments without crypto API
      this.nonce = btoa(Math.random().toString()).substring(2, 18);
    }
  }

  /**
   * Get the current nonce value
   */
  getCurrentNonce(): string {
    return this.nonce || '';
  }

  /**
   * Refresh the nonce (should be called on each page load)
   */
  refreshNonce(): string {
    this.generateNonce();
    return this.getCurrentNonce();
  }

  /**
   * Build CSP header string from directives
   */
  private buildCSPHeader(directives: CSPDirectives, nonce?: string): string {
    const parts: string[] = [];

    Object.entries(directives).forEach(([directive, values]) => {
      if (typeof values === 'boolean') {
        if (values) {
          parts.push(directive.replace(/([A-Z])/g, '-$1').toLowerCase());
        }
      } else if (Array.isArray(values) && values.length > 0) {
        let directiveValues = [...values];

        // Add nonce to script-src and style-src if provided
        if (nonce && (directive === 'script-src' || directive === 'style-src')) {
          directiveValues.push(`'nonce-${nonce}'`);
        }

        const directiveName = directive.replace(/([A-Z])/g, '-$1').toLowerCase();
        parts.push(`${directiveName} ${directiveValues.join(' ')}`);
      }
    });

    return parts.join('; ');
  }

  /**
   * Get CSP header for current environment
   */
  getCSPHeader(environment: 'development' | 'production' = 'development'): string {
    const directives = this.config[environment];
    const header = this.buildCSPHeader(directives, this.nonce || undefined);

    if (this.config.reportUri) {
      return `${header}; report-uri ${this.config.reportUri}`;
    }

    return header;
  }

  /**
   * Get CSP header in report-only mode
   */
  getCSPReportOnlyHeader(environment: 'development' | 'production' = 'development'): string {
    return this.getCSPHeader(environment).replace(/^/, 'Content-Security-Policy-Report-Only: ');
  }

  /**
   * Apply CSP to document (client-side)
   */
  applyToDocument(environment: 'development' | 'production' = 'development'): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Remove existing CSP meta tags
    const existingMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (existingMeta) {
      existingMeta.remove();
    }

    // Create new CSP meta tag
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = this.getCSPHeader(environment);
    document.head.appendChild(meta);
  }

  /**
   * Validate if a resource URL is allowed by CSP
   */
  isResourceAllowed(resourceType: keyof CSPDirectives, url: string, environment: 'development' | 'production' = 'development'): boolean {
    const directives = this.config[environment];
    const allowedSources = directives[resourceType] || directives['default-src'] || [];

    // Check if URL matches any allowed source
    return allowedSources.some(source => {
      if (source === "'self'") {
        return url.startsWith(window.location.origin);
      }
      if (source === "'none'") {
        return false;
      }
      if (source.startsWith('data:')) {
        return url.startsWith('data:');
      }
      if (source.startsWith('blob:')) {
        return url.startsWith('blob:');
      }
      if (source.includes('*')) {
        const regex = new RegExp(source.replace(/\*/g, '.*'));
        return regex.test(url);
      }
      return url.startsWith(source);
    });
  }

  /**
   * Report CSP violation
   */
  reportViolation(violation: {
    documentURI: string;
    referrer: string;
    blockedURI: string;
    violatedDirective: string;
    originalPolicy: string;
    sourceFile?: string;
    lineNumber?: number;
    columnNumber?: number;
  }): void {
    if (this.config.reportUri) {
      fetch(this.config.reportUri, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/csp-report'
        },
        body: JSON.stringify({
          'csp-report': violation
        })
      }).catch(error => {
        console.error('Failed to report CSP violation:', error);
      });
    }
  }

  /**
   * Set up CSP violation event listener
   */
  setupViolationReporting(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.addEventListener('securitypolicyviolation', (event) => {
      this.reportViolation({
        documentURI: event.documentURI,
        referrer: event.referrer,
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        originalPolicy: event.originalPolicy,
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        columnNumber: event.columnNumber
      });
    });
  }

  /**
   * Update CSP configuration
   */
  updateConfig(newConfig: Partial<CSPConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Add allowed source to directive
   */
  addAllowedSource(directive: keyof CSPDirectives, source: string, environment: 'development' | 'production' | 'both' = 'both'): void {
    const environments = environment === 'both' ? ['development', 'production'] as const : [environment];

    environments.forEach(env => {
      const directives = this.config[env];
      if (!directives[directive]) {
        directives[directive] = [];
      }
      if (Array.isArray(directives[directive]) && !directives[directive]!.includes(source)) {
        directives[directive]!.push(source);
      }
    });
  }

  /**
   * Remove allowed source from directive
   */
  removeAllowedSource(directive: keyof CSPDirectives, source: string, environment: 'development' | 'production' | 'both' = 'both'): void {
    const environments = environment === 'both' ? ['development', 'production'] as const : [environment];

    environments.forEach(env => {
      const directives = this.config[env];
      if (Array.isArray(directives[directive])) {
        directives[directive] = directives[directive]!.filter(s => s !== source);
      }
    });
  }
}

// Singleton instance
export const cspService = CSPService.getInstance();

export default cspService;